<template>
    <div class="teams-page">
        <page-hero title="Teams" eyebrow="Commercial League" :subtitle="teams.length ? `${teams.length} ομάδες` : null">
            <div class="teams-page__search">
                <v-text-field
                    v-model="search"
                    dark
                    dense
                    outlined
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    label="Αναζήτηση ομάδας"
                />
            </div>
        </page-hero>

        <div class="cl-page">
            <!-- one league group at a time, or all of them -->
            <div v-if="sections.length > 1" class="teams-page__groups">
                <button
                    v-for="option in groupOptions"
                    :key="option.key"
                    type="button"
                    class="teams-page__group"
                    :class="{ 'teams-page__group--active': selectedGroup === option.key }"
                    @click="selectedGroup = option.key"
                >
                    {{ option.title }}
                    <span class="teams-page__group-count">{{ option.count }}</span>
                </button>
            </div>

            <!-- while the teams load (all of them, it takes a while): tiles of the same shape, shimmering -->
            <section v-if="!loaded" class="teams-section" aria-busy="true" aria-label="Φόρτωση ομάδων">
                <div class="teams-section__head">
                    <span class="teams-section__bar" />
                    <span class="teams-skeleton__bar teams-skeleton__bar--title" />
                </div>
                <div class="teams-page__grid">
                    <div v-for="n in 12" :key="n" class="team-tile team-tile--skeleton" :style="{ animationDelay: `${(n % 4) * 0.12}s` }">
                        <span class="team-tile__stripe" />
                        <span class="teams-skeleton__logo" />
                        <span class="team-tile__text">
                            <span class="teams-skeleton__bar" :style="{ width: `${55 + ((n * 17) % 35)}%` }" />
                            <span class="teams-skeleton__bar teams-skeleton__bar--small" />
                        </span>
                    </div>
                </div>
            </section>

            <div v-else-if="failed" class="cl-empty">Οι ομάδες δεν φόρτωσαν. Δοκίμασε ξανά σε λίγο.</div>
            <div v-else-if="!visibleSections.length" class="cl-empty">Δεν βρέθηκαν ομάδες.</div>

            <section v-for="section in visibleSections" :key="section.key" class="teams-section">
                <div class="teams-section__head">
                    <span class="teams-section__bar" />
                    <span class="teams-section__title">{{ section.title }}</span>
                    <span class="teams-section__count">{{ section.teams.length }} {{ section.teams.length === 1 ? 'ομάδα' : 'ομάδες' }}</span>
                </div>
                <div class="teams-page__grid">
                    <router-link
                        v-for="item in section.teams"
                        :key="item.id"
                        :to="{ name: 'Team', params: { id: item.id } }"
                        class="team-tile"
                        :style="{ '--team-color': item.color || '#009688' }"
                    >
                        <span class="team-tile__stripe" />
                        <span class="cl-logo team-tile__logo">
                            <img v-if="item.logo" :src="`${assetsUrl}/${item.logo}`" :alt="item.name" loading="lazy">
                            <img v-else :src="require('@/assets/main_logo.png')" alt="" loading="lazy">
                        </span>
                        <span class="team-tile__text">
                            <span class="team-tile__name">{{ item.name }}</span>
                            <span v-if="leaguesOf(item, section.groupId).length" class="team-tile__leagues">
                                {{ leaguesOf(item, section.groupId).join(' · ') }}
                            </span>
                        </span>
                        <v-icon class="team-tile__go">mdi-chevron-right</v-icon>
                    </router-link>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
    import teamService from '@/services/team'
    import PageHero from '@/components/PageHero'

    export default {
        components: { PageHero },
        data() {
            return {
                teams: [],
                search: null,
                selectedGroup: 'all',
                loaded: false,
                failed: false,
                page: 1,
                size: 1000,
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            leagueGroups() {
                return this.$store.getters.getLeagueGroups || []
            },
            // one section per league group (in the order of the menu); a team of two groups is in both,
            // the teams without a league at the end
            sections() {
                const teams = this.filteredTeams
                const inGroup = groupId => teams.filter(team => (team.leagues || []).some(league => league.leagueGroup && league.leagueGroup.id === groupId))
                const ordered = [...this.leagueGroups]
                // groups that only the teams know of
                teams.forEach(team => (team.leagues || []).forEach(league => {
                    // an inactive group is not shown on the site
                    if (league.leagueGroup && league.leagueGroup.active !== false && !ordered.some(group => group.id === league.leagueGroup.id)) {
                        ordered.push(league.leagueGroup)
                    }
                }))
                const sections = ordered
                    .map(group => ({ key: `group-${group.id}`, groupId: group.id, title: group.title, teams: inGroup(group.id) }))
                    .filter(section => section.teams.length)
                const others = teams.filter(team => !(team.leagues || []).some(league => league.leagueGroup))
                if (others.length) {
                    sections.push({ key: 'others', groupId: null, title: 'Άλλες ομάδες', teams: others })
                }
                return sections
            },
            groupOptions() {
                return [{ key: 'all', title: 'Όλες', count: this.filteredTeams.length }]
                    .concat(this.sections.map(section => ({ key: section.key, title: section.title, count: section.teams.length })))
            },
            visibleSections() {
                if (this.selectedGroup === 'all') return this.sections
                const section = this.sections.filter(item => item.key === this.selectedGroup)
                // the search left the chosen group empty: show what it found
                return section.length ? section : this.sections
            },
            filteredTeams() {
                if (!this.search) {
                    return this.teams
                }
                const search = this.search.toLowerCase()
                return this.teams.filter(team => team.name.toLowerCase().includes(search))
            }
        },
        created() {
            this.$store.dispatch('ensureLeagueGroups')
            this.fetchAllTeams()
        },
        methods: {
            // the leagues of a team inside one group (all of them for the teams of no group)
            leaguesOf(team, groupId) {
                return (team.leagues || [])
                    .filter(league => groupId === null || (league.leagueGroup && league.leagueGroup.id === groupId))
                    .map(league => league.title)
            },
            fetchAllTeams() {
                teamService.getTeams(this.page, this.size).then(resp => {
                    this.teams = resp.data.content.sort((a, b) => a.name.localeCompare(b.name)).filter(item => item.name !== 'PLAYER POOL') // Filter out "PLAYER POOL" team
                }).catch(() => {
                    this.failed = true
                }).finally(() => {
                    this.loaded = true
                })
            }
        }
    }
</script>

<style scoped>
.teams-page__search {
    max-width: 360px;
}

.teams-page__groups {
    display: flex;
    gap: 8px;
    margin-bottom: 26px;
    padding: 2px 2px 6px;
    overflow-x: auto;
    scrollbar-width: none;
}

.teams-page__groups::-webkit-scrollbar {
    display: none;
}

.teams-page__group {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    padding: 7px 14px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid rgba(35, 35, 35, 0.12);
    color: #232323;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    white-space: nowrap;
    transition: background 0.15s, border-color 0.15s;
}

.teams-page__group:hover {
    border-color: #009688;
}

.teams-page__group--active,
.teams-page__group--active:hover {
    background: #232323;
    border-color: #232323;
    color: #fff;
    box-shadow: inset 0 -3px 0 #03e5b7;
}

.teams-page__group-count {
    padding: 0 7px;
    border-radius: 999px;
    background: rgba(35, 35, 35, 0.08);
    font-size: 0.72rem;
}

.teams-page__group--active .teams-page__group-count {
    background: rgba(255, 255, 255, 0.15);
}

.teams-section + .teams-section {
    margin-top: 36px;
}

.teams-section__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
}

.teams-section__bar {
    width: 14px;
    height: 24px;
    border-radius: 3px;
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    transform: skewX(-14deg);
}

.teams-section__title {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.8rem;
    line-height: 1;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #232323;
}

.teams-section__count {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.82rem;
    color: rgba(35, 35, 35, 0.55);
}

.teams-page__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 14px;
}

.team-tile {
    position: relative;
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 88px;
    padding: 12px 12px 12px 22px;
    border-radius: 10px;
    background: #232323;
    color: #fff !important;
    text-decoration: none;
    box-shadow: 0 6px 18px rgba(35, 35, 35, 0.12);
    overflow: hidden;
    transition: transform 0.15s, box-shadow 0.15s;
}

/* ---------- loading ---------- */
.team-tile--skeleton {
    pointer-events: none;
    animation: teams-pulse 1.3s ease-in-out infinite;
}

.team-tile--skeleton .team-tile__stripe {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    opacity: 0.55;
}

.teams-skeleton__logo {
    position: relative;
    flex: 0 0 auto;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: #3a3a3a;
    overflow: hidden;
}

.teams-skeleton__bar {
    position: relative;
    display: block;
    height: 16px;
    border-radius: 4px;
    background: #3a3a3a;
    overflow: hidden;
}

.teams-skeleton__bar--small {
    width: 35%;
    height: 10px;
}

.teams-skeleton__bar--title {
    width: 180px;
    height: 24px;
    background: rgba(35, 35, 35, 0.1);
}

/* a light sweeping over the placeholders */
.teams-skeleton__logo::after,
.teams-skeleton__bar::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent);
    transform: translateX(-100%);
    animation: teams-shimmer 1.3s ease-in-out infinite;
}

@keyframes teams-shimmer {
    to {
        transform: translateX(100%);
    }
}

@keyframes teams-pulse {
    50% {
        opacity: 0.75;
    }
}

@media (prefers-reduced-motion: reduce) {
    .team-tile--skeleton,
    .teams-skeleton__logo::after,
    .teams-skeleton__bar::after {
        animation: none;
    }
}

.team-tile:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(3, 122, 222, 0.2);
}

/* the colour of the team, as a slanted band on the left */
.team-tile__stripe {
    position: absolute;
    left: -14px;
    top: 0;
    bottom: 0;
    width: 26px;
    background: var(--team-color);
    transform: skewX(-12deg);
}

.team-tile__logo {
    position: relative;
    width: 60px;
    height: 60px;
}

.team-tile__text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1 1 auto;
    min-width: 0;
}

.team-tile__name {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.35rem;
    line-height: 1.1;
    letter-spacing: 0.02em;
    overflow-wrap: anywhere;
}

.team-tile__leagues {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.55);
}

.team-tile__go {
    color: rgba(255, 255, 255, 0.35) !important;
}

.team-tile:hover .team-tile__go {
    color: #03e5b7 !important;
}
</style>
